import { PressableScale } from "pressto";
import { View, Text, Card } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import type { AlarmSilence } from "@src/services/api/types";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import { formatTime12 } from "@src/utils/format-date";
import { format, isToday, isTomorrow } from "date-fns";
import { Icon, IconNameType } from "@src/components/Icon";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import { FC, memo } from "react";

interface SilencePreviewProps {
  silence: AlarmSilence;
  timezone: string;
  onPress?: () => void;
  onDelete?: () => void;
  accessoryIcon?: IconNameType;
}

function formatSilenceDuration(silence: AlarmSilence, timezone: string): string {
  const startDateTime = dateTimeRecordToLocalisedDateTime(silence.period.start, timezone);
  const endDateTime = dateTimeRecordToLocalisedDateTime(silence.period.end, timezone);

  const durationMinutes = Math.round(endDateTime.diff(startDateTime, "minutes").minutes);

  if (durationMinutes < 60) {
    return `${durationMinutes} minute${durationMinutes !== 1 ? "s" : ""}`;
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  if (minutes === 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }

  return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
}

function formatSilencePeriod(silence: AlarmSilence, timezone: string): { time: string; date: string } {
  const startDateTime = dateTimeRecordToLocalisedDateTime(silence.period.start, timezone);
  const startDate = startDateTime.toJSDate();
  const endDateTime = dateTimeRecordToLocalisedDateTime(silence.period.end, timezone);
  const endDate = endDateTime.toJSDate();

  const startTimeStr = formatTime12(startDateTime.hour, startDateTime.minute);
  const endTimeStr = formatTime12(endDateTime.hour, endDateTime.minute);

  const getDateStr = (date: Date) => {
    if (isToday(date)) return "today";
    if (isTomorrow(date)) return "tomorrow";
    return format(date, "MMM d");
  };

  const startDateStr = getDateStr(startDate);
  const endDateStr = getDateStr(endDate);

  if (startDateStr === endDateStr) {
    return { time: `${startTimeStr} - ${endTimeStr}`, date: toSentenceCase(startDateStr) };
  }

  return {
    time: `${startTimeStr} - ${endTimeStr}`,
    date: `${toSentenceCase(startDateStr)} - ${toSentenceCase(endDateStr)}`,
  };
}

export const SilencePreview: FC<SilencePreviewProps> = memo(
  ({ silence, timezone, onPress, onDelete, accessoryIcon }) => {
    const { theme } = useUnistyles();
    const { time, date } = formatSilencePeriod(silence, timezone);
    const duration = formatSilenceDuration(silence, timezone);

    return (
      <PressableScale onPress={onPress}>
        <Card>
          <View flexDirection="row" justifyContent="space-between">
            <View flex={1}>
              <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
                <View width={20} height={20} borderRadius={4} bg="text" />
                <Text textStyle="sm/medium">Silence</Text>
              </View>
              <View gap="sm">
                {silence.reason && (
                  <Text textStyle="xs/regular" color="textMuted">
                    {silence.reason}
                  </Text>
                )}
                <View flexDirection="row" alignItems="center" gap="xs">
                  <View flexDirection="row" alignItems="center" gap="xs">
                    <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                    <Text textStyle="xs/regular" color="textMuted">
                      {date}
                    </Text>
                  </View>
                  <Text textStyle="xs/regular" color="textMuted">
                    •
                  </Text>
                  <View flexDirection="row" alignItems="center" gap="xs">
                    <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                    <Text textStyle="xs/regular" color="textMuted">
                      {time}
                    </Text>
                  </View>
                </View>
                <Text textStyle="xs/regular" color="textMuted">
                  Sirens and strobes disabled
                </Text>
              </View>
            </View>
            <View flexDirection="column" alignItems="center" gap="md">
              {onDelete && (
                <PressableScale onPress={onDelete} hitSlop={8}>
                  <Icon name="delete" color={theme.colors.icon} />
                </PressableScale>
              )}
              {onPress && accessoryIcon && <Icon name={accessoryIcon} size={20} color={theme.colors.icon} />}
            </View>
          </View>
        </Card>
      </PressableScale>
    );
  },
);
